import { InFlightValue } from '@/list/components/cells/in-flight-value';
import { useComplaintsStatus } from '@/utils/useComplaintsStatus';
import dayjs from 'dayjs';
import { useCallback } from 'react';
import { SelectPicker } from 'rsuite';
import { ComplaintStreetAddress } from './modules/complaint-street-address';
import { Column, Container, Data, Value } from './shared/styles';
import { TComplaintData, TUpdateComplaintStatusFn } from './types';

type TComplaintStatsProps = {
  complaintData: TComplaintData;
  updateComplaintStatus: TUpdateComplaintStatusFn;
};

export function ComplaintStats(props: TComplaintStatsProps) {
  const { complaintData, updateComplaintStatus } = props;

  const { allStatusDescriptions, complaintStatusLabelFactory } =
    useComplaintsStatus();

  const selectPickerData = Object.keys(allStatusDescriptions).map((status) => ({
    label: complaintStatusLabelFactory(status),
    value: status,
  }));

  const handleStatusChange = useCallback(
    (updatedStatus) => {
      if (!complaintData.complaintId) return;
      updateComplaintStatus(complaintData.complaintId, updatedStatus);
    },
    [complaintData.complaintId, updateComplaintStatus]
  );

  return (
    <Container>
      <Column>
        <Data data-cy="complaint-details-complaint-date-time-raised">
          <div>Raised</div>
          <Value>
            {dayjs(complaintData.submissionDate).format('YYYY-MM-DD LT')}
          </Value>
        </Data>

        <ComplaintStreetAddress
          streetAddress={complaintData.streetAddress}
          propertyDeckardId={complaintData.propertyDeckardId}
        />
      </Column>

      <Column>
        <Column data-cy="complaint-details-complaint-status">
          <div>Status</div>
          <SelectPicker
            data-cy="complaint-details-complaint-status-picker"
            cleanable={false}
            searchable={false}
            /**
             * "updateComplaintStatus" leads to both "userUpdatedComplaintStatusSaga" and "updateComplaintStatus" being invoked.
             *
             * "userUpdatedComplaintStatusSaga" will call the API to initiate the status change process
             * "updateComplaintStatus" will then update the part of Redux state that holds the complaint data
             *    The part that the "disabled" field of the "SelectPicker" component is interested in is the "status" field
             *    The field can either be a string or an instance of the "InFlightValue" class.
             *      We want to disable the component to prevent users from attempting a status change while one is already underway.
             *      To do this, the client will check if the value of "status" is an instance of the "InFlightValue" class.
             */
            disabled={complaintData.status instanceof InFlightValue}
            data={selectPickerData}
            value={
              complaintData.status instanceof InFlightValue
                ? complaintData.status.value
                : complaintData.status
            }
            onChange={handleStatusChange}
            style={{ width: 224 }}
          />
        </Column>
      </Column>
    </Container>
  );
}
